import { DASHBOARD_TILES } from '@pams/shared';
import type {
  DashboardLayoutConfigDto,
  DashboardShortcutTarget,
  DashboardLayoutConfigEntryDto,
  DashboardLayoutEntryInputDto,
  DashboardTileId,
  TileSize,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE CONFIGURATOR'S EDITING STATE, AS PURE FUNCTIONS. [P6b]
 *
 *  ⚠️ NOT AN ANTD `Form`, AND THAT IS A DEPARTURE FROM `PolicyBuilder` WITH A REASON. The
 *  state here is an ORDERED ARRAY WHOSE IDENTITY MATTERS, and `Form.Item` names ARE indices —
 *  `DutyPointStack`'s whole header is the workaround for that (minted ids plus a hidden
 *  sibling `Form.Item` registering the parent path). There is nothing rc-field-form buys here
 *  that it does not first charge for, and the two `Form.useWatch` traps §8 records — the
 *  registered-fields-only rule and the `JSON.stringify`/`Math.random()` comparison — are both
 *  live on a screen whose preview must re-render on every keystroke.
 *
 *  Validation is not weakened by that: the page parses `toSavePayload(draft)` with the SAME
 *  shared schema the server parses, so there is no hand-typed rule anywhere.
 *
 *  ⚠️ AND `dismissedTileIds` IS NOT COMPUTED HERE. The server derives it as the complement of
 *  what was OFFERED, which is the only place that knows what was offered. A client-sent
 *  exclusion list would be a second answer to "what is off".
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type LayoutDraftEntry = {
  /** ⚠️ [P6b.2] The placement's own identity — minted here, never recomputed. */
  readonly instanceId: string;
  /** ⚠️ [P6b.2] Which row group it sits on, 1-based. */
  readonly row: number;
  readonly tileId: DashboardTileId;
  readonly size: TileSize;
  readonly titleOverride: string | null;
  /** ⚠️ [C1e §3] The tri-state: `null` the registry's sentence, `''` no line, text the text. */
  readonly hintOverride: string | null;
  readonly drillLabelOverride: string | null;
  /** ⚠️ [P6b.1] Only a shortcut has one, and one on a layout must have it before it saves. */
  readonly shortcutTarget: DashboardShortcutTarget | null;
};

export type LayoutDraft = {
  /** ⚠️ THE COMBINATION THIS DRAFT BELONGS TO. See `index.tsx` — a seed effect without it
   *  reseeds the NEW combination from a stale in-flight answer for the OLD one. */
  readonly key: string;
  readonly entries: readonly LayoutDraftEntry[];
};

export const draftKey = (accessRole: string, orgUnitType: string): string => `${accessRole}:${orgUnitType}`;

const toEntry = (row: DashboardLayoutConfigEntryDto): LayoutDraftEntry => ({
  instanceId: row.instanceId ?? mintInstanceId(),
  row: row.row ?? 1,
  tileId: row.id,
  size: row.size,
  titleOverride: row.titleOverride,
  hintOverride: row.hintOverride,
  drillLabelOverride: row.drillLabelOverride,
  shortcutTarget: row.shortcutTarget,
});

export const loadDraft = (config: DashboardLayoutConfigDto): LayoutDraft => ({
  key: draftKey(config.target.accessRole, config.target.orgUnitType),
  entries: config.entries.filter((row) => row.state === 'ON').map(toEntry),
});

/** ⚠️ THE WHOLE ARRAY, IN ORDER. Order is array position — `reorder.ts`' rule. */
export const reorderEntries = (draft: LayoutDraft, order: readonly string[]): LayoutDraft => {
  /* ⚠️ [P6b.2] BY PLACEMENT — two shortcuts share a tile id and a tile-id key would swap the
     wrong one, or drop one of them entirely. */
  const byId = new Map(draft.entries.map((entry) => [entry.instanceId, entry]));
  return {
    ...draft,
    entries: order.flatMap((instanceId) => {
      const entry = byId.get(instanceId);
      return entry ? [entry] : [];
    }),
  };
};

export const patchEntry = (
  draft: LayoutDraft,
  instanceId: string,
  patch: Partial<Omit<LayoutDraftEntry, 'tileId' | 'instanceId'>>,
): LayoutDraft => ({
  ...draft,
  entries: draft.entries.map((entry) => (entry.instanceId === instanceId ? { ...entry, ...patch } : entry)),
});

export const removeEntry = (draft: LayoutDraft, instanceId: string): LayoutDraft => ({
  ...draft,
  entries: draft.entries.filter((entry) => entry.instanceId !== instanceId),
});

/** ⚠️ APPENDS, NEVER INSERTS — `resolveDashboardLayout` step 3's rule, on the client side of
 *  it. The stored order is the operator's, and adding a tile must not move theirs. */
export const addEntry = (
  draft: LayoutDraft,
  tileId: DashboardTileId,
  /**
   * ⚠️ [P6b.1] WHAT A FRESH SHORTCUT POINTS AT, AND WHY IT IS A PARAMETER. A shortcut with no
   * destination is REFUSED by the write path, so a placement added with nothing set would be a
   * tile the operator cannot save and no message explaining it. Only the page knows which role
   * is being configured, so only the page can say what that role can reach.
   */
  reachable: readonly DashboardShortcutTarget[] = [],
): LayoutDraft =>
  /*
   * ⚠️ [P6b.2] A REPEATABLE TILE MAY BE ADDED AGAIN; EVERY OTHER MAY NOT. That asymmetry is
   * read from the CATALOGUE rather than from a hard-coded id, so a second repeatable tile
   * costs this function nothing.
   */
  !DASHBOARD_TILES[tileId].repeatable && draft.entries.some((entry) => entry.tileId === tileId)
    ? draft
    : {
        ...draft,
        entries: [
          ...draft.entries,
          {
            instanceId: mintInstanceId(),
            /* Appended to the LAST row rather than opening a new one — step 3's rule. */
            row: draft.entries.reduce((highest, entry) => Math.max(highest, entry.row), 1),
            tileId,
            size: DASHBOARD_TILES[tileId].size,
            titleOverride: null,
            hintOverride: null,
            drillLabelOverride: null,
            shortcutTarget: DASHBOARD_TILES[tileId].repeatable ? (reachable[0] ?? null) : null,
          },
        ],
      };

/**
 * ⚠️ [P6b.2] MOVE A PLACEMENT TO A ROW. The array order is untouched — a row is a GROUP, and
 * reordering within it is `reorderEntries`' job.
 */
export const setRow = (draft: LayoutDraft, instanceId: string, row: number): LayoutDraft => ({
  ...draft,
  entries: draft.entries.map((entry) => (entry.instanceId === instanceId ? { ...entry, row } : entry)),
});

/**
 * ⚠️ MINTED FROM `crypto.getRandomValues`, NEVER `randomUUID`. That one is secure-context-only
 * and is `undefined` over plain `http://` to anything that is not localhost — CLAUDE.md §8's
 * banned-API rule, which no test can see because jsdom provides all of them unconditionally.
 * `mintShiftId` is the precedent and this is the same four bytes.
 */
export const mintInstanceId = (): string => {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * ⚠️ A BLANK FIELD BECOMES `null`, HERE AND ONLY HERE. `''` is refused by the wire and means
 * "no name" to a reader; `null` means "use the registry's own word". Normalising at the
 * boundary is also what makes `isDirty` below able to say that typing a title and clearing it
 * again is not a change.
 */
const trimmed = (value: string | null): string | null => {
  const text = value?.trim() ?? '';
  return text.length > 0 ? text : null;
};

/**
 * ⚠️ [C1e §3] THE SAME NORMALISATION WITHOUT THE COLLAPSE, for the one field where `''` is a
 * MEANING rather than an absence. Whitespace still trims — `'   '` and `''` are the same
 * statement — but a cleared field reaches the wire as `''` and hides the line.
 */
const suppressible = (value: string | null): string | null => (value === null ? null : value.trim());

export const toSavePayload = (draft: LayoutDraft): { entries: DashboardLayoutEntryInputDto[] } => ({
  entries: draft.entries.map((entry) => ({
    instanceId: entry.instanceId,
    row: entry.row,
    id: entry.tileId,
    size: entry.size,
    titleOverride: trimmed(entry.titleOverride),
    /*
     * ⚠️ [C1e §3] **NOT** `trimmed()`. That collapses `''` to `null`, which is the OPPOSITE
     * statement here — it would make "hide this line" unsendable, and `isDirty` below would
     * then read hiding a line as no edit at all.
     */
    hintOverride: suppressible(entry.hintOverride),
    drillLabelOverride: trimmed(entry.drillLabelOverride),
    shortcutTarget: entry.shortcutTarget,
  })),
});

/** ⚠️ COMPARED ON THE PAYLOAD, NOT ON THE DRAFT, so `''` and `null` are the same edit. */
export const isDirty = (draft: LayoutDraft | null, loaded: LayoutDraft | null): boolean =>
  draft !== null && loaded !== null && JSON.stringify(toSavePayload(draft)) !== JSON.stringify(toSavePayload(loaded));

/**
 * §5 — "N tiles not on this layout", with the new ones marked.
 *
 * ⚠️ "NEW" MEANS NOTHING UNTIL A HUMAN HAS DECIDED SOMETHING, AND THAT IS WHY `configured`
 * GATES IT. On a freshly seeded row every analytical tile is in neither `tileIds` nor
 * `dismissedTileIds` — so "never decided about" is true of eight tiles on a system nobody has
 * touched, and a badge on eight of fourteen says nothing at all. Once an operator HAS arranged
 * the layout, a tile in neither list is genuinely one that arrived after they last looked,
 * which is exactly the case §5 exists for: nobody browses a picker for things they do not know
 * exist.
 */
export const availableToAdd = (config: DashboardLayoutConfigDto): DashboardLayoutConfigEntryDto[] =>
  config.entries.filter((row) => row.state === 'DISMISSED' || row.state === 'UNDECIDED');

export const isNewTile = (config: DashboardLayoutConfigDto, row: DashboardLayoutConfigEntryDto): boolean =>
  config.configured && row.state === 'UNDECIDED';

export const unavailableTiles = (config: DashboardLayoutConfigDto): DashboardLayoutConfigEntryDto[] =>
  config.entries.filter((row) => row.state === 'UNAVAILABLE');
